import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    Box,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    useGetIndividualUserInfoQuery,
    useSendInviteMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import LoadingComponent from '../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../shared/AuthRoles';
import InviteModal from '../../pages/Guest Pages/InviteModal';
import toast from '../../shared/Toast';
import EditUserForm from './User Forms/EditUserForm';
import UserSummary from './UserSummary';
import UserViewReadOnlyPanels from './UserViewReadOnlyPanels';
import { parseUsersRouteUserId } from './userListQueryUrl';
import { AdminRole, ContractorRole } from '../../enums/users';
import { trackEvent } from '../../../app/Analytics';

export default function ViewIndividualUser() {
    const loggedInUser = UserRole();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [editScope, setEditScope] = useState<'personal' | 'login' | null>(
        null,
    );

    const profileUserId = useMemo(
        () => parseUsersRouteUserId(location.pathname),
        [location.pathname],
    );
    const userUrl = useMemo(() => {
        if (!profileUserId) return '';
        /** 7-day platform admin: no `company` on token; user detail still lives under `/7-day/users/`. */
        if (loggedInUser?.role === AdminRole.Admin) {
            return `/7-day/users/${profileUserId}`;
        }
        if (loggedInUser?.company == null) return '';
        if (
            loggedInUser?.role === 'owner_admin' ||
            loggedInUser?.role === 'owner_company'
        ) {
            return `/owners/${loggedInUser.company}/users/${profileUserId}`;
        }
        if (
            loggedInUser?.role === 'guest_admin' ||
            loggedInUser?.role === 'guest_company' ||
            loggedInUser?.role === 'guest_department_admin'
        ) {
            return `/guests/${loggedInUser.company}/users/${profileUserId}`;
        }
        if (loggedInUser?.role === ContractorRole.Admin) {
            return `/contractors/${loggedInUser.company}/users/${profileUserId}`;
        }
        return `/7-day/users/${profileUserId}`;
    }, [loggedInUser, profileUserId]);

    const {
        data: getIndividualUser,
        isFetching,
        isError,
    } = useGetIndividualUserInfoQuery(userUrl, { skip: !userUrl });

    const isMobile = useMediaQuery('(max-width: 770px)');
    const [sendInvite] = useSendInviteMutation();
    const [openInviteModal, setInviteOpen] = useState(false);
    const viewOnly = getIndividualUser?.viewOnly;
    const theme = useTheme();

    useEffect(() => {
        if (searchParams.get('edit') !== '1') return;
        setEditScope('personal');
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!userUrl || !isError) return;
        navigate(`/permissionDenied`, {
            state: {
                name: 'individualUser',
            },
        });
    }, [userUrl, isError, navigate]);

    useEffect(() => {
        if (
            location?.state?.name === 'newlyAddedUser' &&
            getIndividualUser &&
            !getIndividualUser?.invite?.accepted
        ) {
            setInviteOpen(true);
        } else {
            setInviteOpen(false);
        }
    }, [location.state, getIndividualUser]);
    const clearNewlyAddedUserLocationState = useCallback(() => {
        if (location.state?.name !== 'newlyAddedUser') return;
        navigate(
            {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
            },
            { replace: true, state: {} },
        );
    }, [
        location.pathname,
        location.search,
        location.hash,
        location.state?.name,
        navigate,
    ]);

    const getIndividualUserRef = useRef(getIndividualUser);
    const profileUserIdRef = useRef(profileUserId);
    getIndividualUserRef.current = getIndividualUser;
    profileUserIdRef.current = profileUserId;

    const sendInvitationForProfileUser = useCallback(async () => {
        const raw =
            getIndividualUserRef.current?.id ?? profileUserIdRef.current;
        const id = parseInt(String(raw), 10);
        if (Number.isNaN(id)) {
            throw new Error('Invalid user id');
        }
        await sendInvite({ id }).unwrap();
    }, [sendInvite]);

    const handleCloseInviteModal = () => {
        setInviteOpen(false);
        clearNewlyAddedUserLocationState();
    };

    const teamReturnPath =
        typeof location.state?.returnTo === 'string' &&
        /^\/team\/\d+\/edit$/.test(location.state.returnTo)
            ? location.state.returnTo
            : null;

    const onClickGoBack = () => {
        navigate(teamReturnPath ?? '/users');
    };

    const onClickSendInvite = () => {
        trackEvent(
            'Users',
            'Send Invite',
            `Send invite Clicked - (${loggedInUser?.role})`,
        );

        setInviteOpen(false);
        sendInvitationForProfileUser()
            .then(() => {
                toast.success('Invite link has been sent to user');
                try {
                    clearNewlyAddedUserLocationState();
                } catch {
                    // Do not treat navigation/state cleanup failures as a failed resend.
                }
            })
            .catch(() => {
                toast.error(
                    'Failed to send invite link, please check email is correct',
                );
            });
    };

    return (
        <>
            {isFetching === true ? (
                <Grid
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        alignContent: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <LoadingComponent />
                </Grid>
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        bgcolor: (t) => t.palette.neutral.main,
                        minHeight: isMobile ? '100%' : 'auto',
                        py: { xs: 2, sm: 3 },
                        pb: { xs: 4, sm: 6 },
                    }}
                >
                    <Stack spacing={3} sx={{ width: '100%' }}>
                        <Typography
                            variant='h1'
                            sx={{ textTransform: 'uppercase' }}
                        >
                            User profile
                        </Typography>
                        <UserSummary
                            user={getIndividualUser}
                            userUrl={userUrl}
                            canEditPhoto={
                                Boolean(userUrl && getIndividualUser?.id) &&
                                !loggedInUser?.role?.includes('company') &&
                                loggedInUser?.role !== 'admin'
                            }
                        />
                        {editScope && getIndividualUser ? (
                            <EditUserForm
                                user={getIndividualUser}
                                embedded
                                embeddedEditScope={editScope}
                                onEmbeddedCancel={() => setEditScope(null)}
                                onEmbeddedSaved={() => setEditScope(null)}
                                embeddedCanSendInvite={
                                    !getIndividualUser?.lastLoginAt
                                }
                                onEmbeddedSendOrResendInvite={onClickSendInvite}
                            />
                        ) : (
                            <UserViewReadOnlyPanels
                                user={getIndividualUser}
                                loggedInRole={loggedInUser?.role}
                                viewOnly={viewOnly}
                                onBeginEditPersonal={() =>
                                    setEditScope('personal')
                                }
                                onBeginEditLogin={() => setEditScope('login')}
                                canEdit
                                onSendInvite={onClickSendInvite}
                                canSendInvite={!getIndividualUser?.lastLoginAt}
                                invite={getIndividualUser?.invite}
                            />
                        )}
                        <Stack direction='row' sx={{ pt: 0.5 }}>
                            <CustomButton
                                onClick={onClickGoBack}
                                color='primary'
                                sx={{
                                    marginLeft: '-8px',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        color: theme.palette.textAccent.main,
                                        backgroundColor: 'transparent',
                                        background: 'none',
                                    },
                                }}
                            >
                                <ArrowBackIcon />{' '}
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        paddingLeft: '5px',
                                    }}
                                >
                                    {teamReturnPath
                                        ? 'Back to team'
                                        : 'Back to users'}
                                </Typography>
                            </CustomButton>
                        </Stack>
                    </Stack>
                </Box>
            )}
            <InviteModal
                open={openInviteModal}
                onClose={handleCloseInviteModal}
            />
        </>
    );
}
